import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { googleDriveApi, learningHooksApi, tasksApi } from '../../services/api';

interface LearningHook {
  id: number;
  title: string;
}

interface Task {
  id: number;
  learning_hook: number;
  stage: string;
  title: string;
  instructions: string;
  rubric: string;
  accepted_evidence_types?: string[];
  google_template_file_id?: string | null;
  google_template_name?: string | null;
  google_template_mime_type?: string | null;
  order: number;
  is_required: boolean;
  is_active: boolean;
}

const STAGES = ['why', 'define', 'do', 'explain', 'apply', 'reflect'];
const EVIDENCE_TYPES: { value: string; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'link', label: 'Link' },
  { value: 'google_drive', label: 'Google Drive' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'camera', label: 'Camera' },
  { value: 'note_time_log', label: 'Note/Time Log' },
];

export const TaskManagerPanel: React.FC = () => {
  const [hooks, setHooks] = useState<LearningHook[]>([]);
  const [selectedHookId, setSelectedHookId] = useState<number | null>(null);

  const [tasks, setTasks] = useState<Task[]>([]);

  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);

  const [deletingTaskId, setDeletingTaskId] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newStage, setNewStage] = useState('define');
  const [newTitle, setNewTitle] = useState('');
  const [newInstructions, setNewInstructions] = useState('');
  const [newRequired, setNewRequired] = useState(true);
  const [newOrder, setNewOrder] = useState(0);
  const [newActive, setNewActive] = useState(true);
  const [newAcceptedEvidenceTypes, setNewAcceptedEvidenceTypes] = useState<string[]>(['text']);

  const [newTemplateFile, setNewTemplateFile] = useState<{ id: string; name: string; mimeType?: string } | null>(null);

  const isEditing = editingTaskId != null;

  const hookTitle = useMemo(() => {
    const h = hooks.find((x) => x.id === selectedHookId);
    return h?.title || '';
  }, [hooks, selectedHookId]);

  const fetchHooks = useCallback(async () => {
    const hooksResponse = await learningHooksApi.getAll();
    const hooksData = hooksResponse.data.learning_hooks || hooksResponse.data || [];
    const minimal = (Array.isArray(hooksData) ? hooksData : []).map((h: any) => ({
      id: h.id,
      title: h.title,
    }));
    setHooks(minimal);
    if (minimal.length > 0 && selectedHookId == null) {
      setSelectedHookId(minimal[0].id);
    }
  }, [selectedHookId]);

  const fetchTasks = useCallback(async (hookId: number) => {
    const res = await tasksApi.list({ hook_id: hookId });
    setTasks(res.data.tasks || []);
  }, []);

  useEffect(() => {
    const boot = async () => {
      setLoading(true);
      setError('');
      setSuccess('');
      try {
        await fetchHooks();
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load learning hooks');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [fetchHooks]);

  useEffect(() => {
    const run = async () => {
      if (!selectedHookId) return;
      setError('');
      setSuccess('');
      try {
        setEditingTaskId(null);
        await fetchTasks(selectedHookId);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to fetch tasks');
      }
    };
    run();
  }, [fetchTasks, selectedHookId]);

  const resetForm = useCallback(() => {
    setEditingTaskId(null);
    setNewStage('define');
    setNewTitle('');
    setNewInstructions('');
    setNewRequired(true);
    setNewOrder(0);
    setNewActive(true);
    setNewAcceptedEvidenceTypes(['text']);
    setNewTemplateFile(null);
  }, []);

  const loadGooglePicker = useCallback(async () => {
    if ((window as any).google?.picker) return;

    await new Promise<void>((resolve, reject) => {
      const existing = document.querySelector('script[data-google-picker="1"]') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load Google API')));
        return;
      }

      const s = document.createElement('script');
      s.src = 'https://apis.google.com/js/api.js';
      s.async = true;
      s.defer = true;
      s.setAttribute('data-google-picker', '1');
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load Google API'));
      document.body.appendChild(s);
    });

    await new Promise<void>((resolve, reject) => {
      (window as any).gapi.load('picker', {
        callback: () => resolve(),
        ontimeout: () => reject(new Error('Failed to load Google Picker')),
        timeout: 10000,
      });
    });
  }, []);

  const openTemplatePicker = useCallback(async () => {
    setError('');
    setSuccess('');

    const devKey = (process.env.REACT_APP_GOOGLE_PICKER_API_KEY || '').trim();
    if (!devKey) {
      setError('Missing REACT_APP_GOOGLE_PICKER_API_KEY (Google Picker API key)');
      return;
    }

    try {
      await loadGooglePicker();
      const tokenRes = await googleDriveApi.accessToken();
      const accessToken = tokenRes?.data?.access_token;
      const pickerAppId = String(tokenRes?.data?.picker_app_id || '');
      if (!accessToken || !/^\d+$/.test(pickerAppId)) {
        setError('Google Picker credentials are incomplete');
        return;
      }

      const picker = new (window as any).google.picker.PickerBuilder()
        .setDeveloperKey(devKey)
        .setOAuthToken(accessToken)
        .setAppId(pickerAppId)
        .setOrigin(window.location.origin)
        .addView(new (window as any).google.picker.DocsView().setIncludeFolders(false))
        .setCallback((data: any) => {
          if (data.action === (window as any).google.picker.Action.PICKED) {
            const doc = data.docs && data.docs[0];
            if (doc?.id) {
              setNewTemplateFile({ id: doc.id, name: doc.name, mimeType: doc.mimeType });
            }
          }
        })
        .build();
      picker.setVisible(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to open Google Picker');
    }
  }, [loadGooglePicker]);

  const handleUpsertTask = async () => {
    if (!selectedHookId) return;
    setError('');
    setSuccess('');

    if (newAcceptedEvidenceTypes.indexOf('google_drive') !== -1 && !newTemplateFile) {
      setError('Please choose a Google Drive template.');
      return;
    }

    try {
      if (isEditing && typeof editingTaskId === 'number') {
        await tasksApi.update(editingTaskId, {
          stage: newStage,
          title: newTitle,
          instructions: newInstructions,
          accepted_evidence_types: newAcceptedEvidenceTypes,
          order: newOrder,
          is_required: newRequired,
          is_active: newActive,
        });

        if (newAcceptedEvidenceTypes.indexOf('google_drive') !== -1 && newTemplateFile) {
          await tasksApi.setDriveTemplate(editingTaskId, {
            google_template_file_id: newTemplateFile.id,
            google_template_name: newTemplateFile.name,
            google_template_mime_type: newTemplateFile.mimeType || null,
          });
        } else {
          await tasksApi.setDriveTemplate(editingTaskId, { google_template_file_id: null });
        }

        setSuccess('Task updated');
        resetForm();
        await fetchTasks(selectedHookId);
        return;
      }

      const createRes = await tasksApi.create({
        learning_hook: selectedHookId,
        stage: newStage,
        title: newTitle,
        instructions: newInstructions,
        accepted_evidence_types: newAcceptedEvidenceTypes,
        order: newOrder,
        is_required: newRequired,
        is_active: newActive,
      });

      const createdId = createRes?.data?.id;

      if (newAcceptedEvidenceTypes.indexOf('google_drive') !== -1 && newTemplateFile && typeof createdId === 'number') {
        await tasksApi.setDriveTemplate(createdId, {
          google_template_file_id: newTemplateFile.id,
          google_template_name: newTemplateFile.name,
          google_template_mime_type: newTemplateFile.mimeType || null,
        });
      }

      setSuccess('Task created');
      resetForm();
      await fetchTasks(selectedHookId);
    } catch (e: any) {
      setError(e?.response?.data?.error || (isEditing ? 'Failed to update task' : 'Failed to create task'));
    }
  };

  const startEdit = useCallback((t: Task) => {
    setError('');
    setSuccess('');
    setEditingTaskId(t.id);
    setNewStage(t.stage || 'define');
    setNewTitle(t.title || '');
    setNewInstructions(t.instructions || '');
    setNewRequired(!!t.is_required);
    setNewOrder(typeof t.order === 'number' ? t.order : 0);
    setNewActive(!!t.is_active);
    setNewAcceptedEvidenceTypes(
      Array.isArray(t.accepted_evidence_types) && t.accepted_evidence_types.length > 0 ? t.accepted_evidence_types : ['text']
    );

    if (t.google_template_file_id) {
      setNewTemplateFile({
        id: t.google_template_file_id,
        name: t.google_template_name || t.google_template_file_id,
        mimeType: t.google_template_mime_type || undefined,
      });
    } else {
      setNewTemplateFile(null);
    }
  }, []);

  const handleDeleteTask = useCallback(
    async (t: Task) => {
      if (!selectedHookId) return;

      setError('');
      setSuccess('');

      const ok = window.confirm(`Delete task "${t.title}"? This cannot be undone.`);
      if (!ok) return;

      try {
        setDeletingTaskId(t.id);
        await tasksApi.delete(t.id);
        if (editingTaskId === t.id) {
          resetForm();
        }
        setSuccess('Task deleted');
        await fetchTasks(selectedHookId);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to delete task');
      } finally {
        setDeletingTaskId(null);
      }
    },
    [editingTaskId, fetchTasks, resetForm, selectedHookId]
  );

  if (loading) {
    return <div className="bg-white rounded-lg shadow p-6">Loading…</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Tasks</h2>
          <p className="text-gray-600 text-sm">Create tasks and attach Google Drive templates.</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Learning Hook</label>
        <select
          value={selectedHookId ?? ''}
          onChange={(e) => setSelectedHookId(Number(e.target.value) || null)}
          className="w-full border rounded px-3 py-2"
        >
          {hooks.map((h) => (
            <option key={h.id} value={h.id}>
              {h.title}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold mb-3">{isEditing ? `Edit Task #${editingTaskId}` : 'Create Task'}</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
              <select value={newStage} onChange={(e) => setNewStage(e.target.value)} className="w-full border rounded px-3 py-2">
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
              <textarea value={newInstructions} onChange={(e) => setNewInstructions(e.target.value)} className="w-full border rounded px-3 py-2" rows={4} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Accepted evidence</label>
              <div className="grid grid-cols-2 gap-2">
                {EVIDENCE_TYPES.map((t) => {
                  const checked = newAcceptedEvidenceTypes.indexOf(t.value) !== -1;
                  return (
                    <label key={t.value} className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const nextChecked = e.target.checked;
                          setNewAcceptedEvidenceTypes((prev) => {
                            const curr = Array.isArray(prev) ? prev : [];
                            if (nextChecked) {
                              return curr.indexOf(t.value) !== -1 ? curr : [...curr, t.value];
                            }
                            const filtered = curr.filter((x) => x !== t.value);
                            return filtered.length > 0 ? filtered : ['text'];
                          });
                        }}
                      />
                      <span>{t.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {newAcceptedEvidenceTypes.indexOf('google_drive') !== -1 && (
              <div className="border rounded p-3 bg-gray-50">
                <div className="text-sm font-medium text-gray-800">Provide a template</div>
                <div className="text-xs text-gray-600 mt-1">Choose a Google Doc/Slides/Sheets file from Drive.</div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <button
                    onClick={openTemplatePicker}
                    className="bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50"
                    type="button"
                  >
                    Choose from Drive
                  </button>
                  <div className="text-xs text-gray-700 truncate">
                    {newTemplateFile ? (
                      <span>
                        Selected: <span className="font-medium">{newTemplateFile.name}</span>
                      </span>
                    ) : (
                      <span>No template selected</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={newRequired} onChange={(e) => setNewRequired(e.target.checked)} />
                <span className="text-sm text-gray-700">Required</span>
              </label>

              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={newActive} onChange={(e) => setNewActive(e.target.checked)} />
                <span className="text-sm text-gray-700">Active</span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Order</span>
                <input
                  type="number"
                  value={newOrder}
                  onChange={(e) => setNewOrder(Number(e.target.value) || 0)}
                  className="w-24 border rounded px-2 py-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleUpsertTask}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                disabled={!selectedHookId || !newTitle.trim()}
              >
                {isEditing ? 'Save Changes' : 'Create Task'}
              </button>

              {isEditing && (
                <button
                  onClick={resetForm}
                  className="bg-white border px-4 py-2 rounded hover:bg-gray-50"
                  type="button"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3">Tasks for: {hookTitle}</h3>
          {tasks.length === 0 ? (
            <div className="text-sm text-gray-500">No tasks yet.</div>
          ) : (
            <div className="space-y-2">
              {tasks
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((t) => (
                  <div key={t.id} className="border rounded p-3">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">#{t.id} [{t.stage}] {t.title}</div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs text-gray-500">order {t.order} • {t.is_required ? 'required' : 'optional'} • {t.is_active ? 'active' : 'inactive'}</div>
                        <button
                          onClick={() => startEdit(t)}
                          className="text-xs px-2 py-1 border rounded bg-white hover:bg-gray-50"
                          type="button"
                          disabled={deletingTaskId === t.id}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTask(t)}
                          className="text-xs px-2 py-1 border rounded bg-white hover:bg-gray-50 text-red-600 border-red-200"
                          type="button"
                          disabled={deletingTaskId === t.id}
                        >
                          {deletingTaskId === t.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Accepted: {(t.accepted_evidence_types && t.accepted_evidence_types.length > 0 ? t.accepted_evidence_types : ['text']).join(', ')}
                    </div>
                    {(t.google_template_file_id || '') && (
                      <div className="text-xs text-gray-500 mt-1">
                        Template: {t.google_template_name || t.google_template_file_id}
                      </div>
                    )}
                    {t.instructions && <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{t.instructions}</div>}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
